import { formatDate } from "date-fns";
import { Spinner } from "@radix-ui/themes";
import { cn } from "@/core/helpers/cn";
import formatPrice from "@/core/helpers/formatPrice";
import { BankCardIcon, CashIcon, Clock3Icon } from "@/shared/icons";
import { BankTransaction } from "@/domains/bank/hooks/useBankTransactionsQuery";
import { BankBranchPagination } from "@/domains/bank/components/BankBranch/components/BankBranchPagination";
import { Paginated } from "@/domains/bank/types";

function getTransactionTitle(transaction: BankTransaction) {
  switch (transaction.type) {
    case "withdraw_cash":
      return "Снятие наличных";

    case "transfer_account":
      if (transaction.operation === "decrement") {
        return "Перевод";
      }
      return "Входящий перевод";

    case "deposit_cash":
      return "Пополнение наличными";

    case "tariff_charge":
      return "Плата за обслуживание";

    default:
      return "Неизвестная операция";
  }
}

const getIcon = (iconType: BankTransaction["type"]) => {
  switch (iconType) {
    case "deposit_cash":
      return <CashIcon className="size-5 text-white/30" />;

    default:
      return <BankCardIcon className="size-5 text-white/30" />;
  }
};

type TransactionHistoryProps = {
  transactions: Paginated<BankTransaction>;
  isRefetching?: boolean;
  className?: string;
  page: number;
  onPageChange: (page: number) => void;
};

export function TransactionHistory({
  transactions,
  isRefetching,
  page,
  onPageChange,
  className,
}: TransactionHistoryProps) {
  const { items, total } = transactions;

  const totalAmount = items.reduce((sum, transaction) => {
    if (transaction.operation === "decrement") {
      return sum - Math.abs(transaction.amount);
    }
    return sum + Math.abs(transaction.amount);
  }, 0);

  return (
    <div className={cn("flex flex-col h-full", className)}>
      <header className="flex items-center justify-between mb-4">
        <h2 className="flex items-center gap-2 text-white text-18 font-bold">
          История операций
          <Spinner
            className={cn(
              "size-4 text-white/50 animate-spin opacity-0 transition-opacity duration-300",
              isRefetching && "opacity-100",
            )}
          />
        </h2>
      </header>

      <div className="flex items-center justify-between mb-4">
        <div className="mb-4">
          <h3 className="text-12 font-bold uppercase text-red-400">ЧЕТВЕРГ, 7 АВГУСТА</h3>
        </div>

        <div
          className={cn("text-18 font-bold", totalAmount < 0 ? "text-red-500" : "text-green-500")}
        >
          {formatPrice(Math.abs(totalAmount))} ₽
        </div>
      </div>

      {items.length === 0 && (
        <div className="flex flex-col gap-4 items-center justify-center h-full bg-[#151515] rounded-xl">
          <Clock3Icon className="size-6 text-white/30" />
          <div className="text-11 font-medium text-white/20 uppercase text-center">
            Нет операций
            <br />
            за выбранный период
          </div>
        </div>
      )}

      {items.length > 0 && (
        <>
          <div
            className="h-[430px] overflow-y-auto pr-4 space-y-3 mb-4 [--scrollbar-thumb-bg:var(--accent-muted)]"
            data-mz-accent="green"
          >
            {items.map((transaction) => (
              <div
                key={transaction.id}
                className="grid grid-cols-[1fr_60px_160px] items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0">{getIcon(transaction.type)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white/70 text-15 font-normal truncate">
                      {getTransactionTitle(transaction)}
                    </div>
                  </div>
                </div>

                <div className="text-white/50 text-14">
                  {formatDate(transaction.createdAt, "hh:mm")}
                </div>

                <div
                  className={cn(
                    "text-14 font-bold text-right",
                    transaction.operation === "decrement" ? "text-red-500" : "text-green-500",
                  )}
                >
                  {transaction.operation === "decrement" ? "-" : "+"}
                  {formatPrice(Math.abs(transaction.amount))} ₽
                </div>
              </div>
            ))}
          </div>

          <BankBranchPagination value={page} totalPages={total / 50} onChange={onPageChange} />
        </>
      )}
    </div>
  );
}
